import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ENDPOINTS from "../../config/apiUrls";
import axiosInstance from "../../config/axiosInstance";
import _ from 'lodash';
import { formatTimestamp, getOwnerEmail, getToken, pick } from "../../helpers/utils";
import qs from 'qs';
import { HUMAN_TIMEZONES } from "../../constants";
import moment from "moment/moment";
import { toast } from "react-toastify";

const HIDDEN_NOTIFICATION_GROUPS = new Set(["radiomax", "radiofx", "radiiofx"]);

const parseStationGroups = (value) =>
  String(value || "")
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean);

const uniqueMergeGroups = (groups) => {
  const seen = new Set();
  const out = [];
  groups.forEach((group) => {
    const key = group.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(group);
  });
  return out;
};

const buildStationGroupPayload = (stationGroupString, selectedGroups = []) => {
  const originalGroups = parseStationGroups(stationGroupString);
  const hiddenGroups = originalGroups.filter((g) => HIDDEN_NOTIFICATION_GROUPS.has(g.toLowerCase()));
  const merged = uniqueMergeGroups([...(selectedGroups || []), ...hiddenGroups]);
  return merged.length ? merged.join(",") : stationGroupString;
};


export const getEventsList = createAsyncThunk(
  'events/getEventsList',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const owner = auth?.user?.attributes?.ownerEmail ?? auth.user?.attributes?.username;

      const { data: data } = await axiosInstance.post(ENDPOINTS.CALENDER.EVENTS.LIST, {
        owner,
        startTime: moment().subtract(20, 'weeks').toISOString(),
        endTime: moment().add(20, 'months').toISOString(),
      });

      const events = data.data;


      const ids = events.map(event => event.id);

      const { data: { data: participants } } = await axiosInstance.post(ENDPOINTS.CALENDER.EVENTS.SUBLIST, {
        filter: { ids },
        token: getToken(),
      });

      const updatedEvents = events.map(event => {
        const count = participants.filter(sub => sub.id === event.id).length;
        return {
          ...event,
          attributes: {
            ...event.attributes,
            followers: count,
          },
        };
      });

      updatedEvents.sort((a, b) => {
        if (a.attributes.title < b.attributes.title) return -1;
        if (a.attributes.title > b.attributes.title) return 1;
        return 0;
      });

      return updatedEvents;

    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
      })
    }
  }
);


export const getEventSubList = createAsyncThunk(
  'events/getEventSubList',
  async (filter, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState();

      const { data: { data } } = await axiosInstance.post(ENDPOINTS.CALENDER.EVENTS.SUBLIST, {
        filter: filter,
        token: getToken(),
      });
      const userIds = data.map(user => user.attributes.username);

      const { data: userMetaData } = await axiosInstance.get(ENDPOINTS.USERS.UPDATE_PROFILE, {
        params: { id: userIds },
        paramsSerializer: params => qs.stringify(params, { indices: false }),
      });

      return (Array.isArray(userMetaData?.data) ? userMetaData?.data : [userMetaData?.data])?.map(user => {
        if (!user) return null;
        let name = user.attributes.firstName != undefined ? user.attributes.firstName + ' ' + user.attributes.lastName : 'Name not available'
        return {
          name,
          userId: user.id || '',
          email: user.attributes.email || 'N/A',
          avatar: user.attributes.avatar || null,
          zipcode: user.attributes.zipcode || 'N/A',
          joinedAt: formatTimestamp(user.attributes.created),
          extraInfo: user.attributes

        };
      }).filter(Boolean);
    } catch (error) {
      console.error('Error fetching events:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const sendNotification = createAsyncThunk(
  'events/sendNotification',
  async (
    { message = 'Notification sent successfully', payload: {  title, body, deeplink ,eventId, stationGroups } },
    { rejectWithValue, getState }
  ) => {
    console.log(eventId,'eventId')
    const {
      auth: {
        user: { attributes },
      },
    } = getState();

    try {
      const selectedStationGroups = Array.isArray(stationGroups) ? stationGroups.filter(Boolean) : [];
      const stationGroupValue = selectedStationGroups.length > 0
        ? buildStationGroupPayload(attributes.stationGroup, selectedStationGroups)
        : attributes.stationGroup;
      const requestData = {
        type: eventId ? 'event' : 'station', // Always include type
        attributes: {
          title,
          body,
          ...(deeplink ? { deeplink } : {}), // Add deeplink if provided
        },
        stationGroup: stationGroupValue,
        stationId: attributes.alias,
        firebaseProject: attributes.firebaseProject,
      };

      if (eventId) {
        requestData.id = eventId;
      }

      const endpoint = eventId
        ? ENDPOINTS.EVENTS.SEND_NOTIFICATION
        : ENDPOINTS.EVENTS.SEND_STATION_NOTIFICATION;

      const response = await axiosInstance.post(endpoint, requestData);

      if (response.status === 204) {
        toast.success(message, {
          position: 'top-right',
          autoClose: 3000,
        });
        return true;
      } else {
        toast.error('Failed to Send Notification.', {
          position: 'top-right',
        });
        return rejectWithValue('Failed to Send Notification');
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong.', {
        position: 'top-right',
      });
      return rejectWithValue(error.message);
    }
  }
);



export const scheduleEventNotification = createAsyncThunk('events/scheduleEventNotification',
  async ({ message = 'Notification Scheduled successfully', palyload: { eventId, title, body, deeplink, tz, time, date, stationGroups } }, { rejectWithValue, getState,dispatch }) => {

    const { auth: { user: { attributes } } } = getState();
    try {

      const owner = getState().auth?.user?.attributes?.ownerEmail ?? getState().auth.user?.attributes?.username;
      const selectedStationGroups = Array.isArray(stationGroups) ? stationGroups.filter(Boolean) : [];
      const stationGroupValue = selectedStationGroups.length > 0
        ? buildStationGroupPayload(attributes.stationGroup, selectedStationGroups)
        : attributes.stationGroup;

      const requestData = {
        token: getToken(),
        stationGroup: stationGroupValue,
        stationId: attributes.alias,
        firebaseProject: attributes.firebaseProject,
        type: 'station',
        ...(deeplink ? { deeplink } : {}),
        eventId,
        message: body,
        subject: title,
        tz: HUMAN_TIMEZONES[tz],
        time,
        date,
        owner
      }

      const response = await axiosInstance.post(ENDPOINTS.CALENDER.EVENTS.SCHEDULE_EVENT_NOTIFICATION, requestData);
      if (response.status === 200) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });
        dispatch(getNotificationsList());
        return true;
      } else {
        toast.error('Failed to Send Notification.', {
          position: "top-right",
        })
      }
    } catch (error) {
      console.log(error.message);
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);


export const updateScheduledNotification = createAsyncThunk('events/updateScheduledNotification',
  async ({ message = 'Notification updated successfully', payload: { eventId, notification_id,title, body, deeplink, tz, time, date, stationGroups } }, { rejectWithValue, getState, dispatch }) => {
    const { auth: { user: { attributes } } } = getState();
    const owner = getState().auth?.user?.attributes?.ownerEmail ?? getState().auth.user?.attributes?.username;
    try {
      const selectedStationGroups = Array.isArray(stationGroups) ? stationGroups.filter(Boolean) : [];
      const stationGroupValue = selectedStationGroups.length > 0
        ? buildStationGroupPayload(attributes.stationGroup, selectedStationGroups)
        : attributes.stationGroup;

      const requestData = {
        token: getToken(),
        stationGroup: stationGroupValue,
        stationId: attributes.alias,
        firebaseProject: attributes.firebaseProject,
        type: 'station',
        ...(deeplink ? { deeplink } : {}),
        eventId,
        notification_id,
        message: body,
        subject: title,
        tz: HUMAN_TIMEZONES[tz],
        time,
        date,
        owner
      }

      const response = await axiosInstance.post(ENDPOINTS.CALENDER.EVENTS.SCHEDULE_EVENT_NOTIFICATION, requestData);
      if (response.status === 200) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        dispatch(getNotificationsList());
      } else {
        toast.error('Failed to Send Notification.', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.log(error.message);
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);



export const getNotificationsList = createAsyncThunk(
  'events/getNotificationsList',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const owner = auth?.user?.attributes?.ownerEmail ?? auth.user?.attributes?.username;
      const token = getToken();
      const { data } = await axiosInstance.post(ENDPOINTS.EVENTS.SCHEDULED_NOTIFICATIONS, {
        token,
        owner,
        filter: {
          page: 1,
          size: 20
        }
      });

      return data;

    } catch (error) {
      rejectWithValue(error)
      toast.error(error.message, {
        position: "top-right",
      })
    }
  }
);


export const deleteScheduledNotification = createAsyncThunk(
  'events/deleteScheduledNotification',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const user = auth?.user?.attributes;
      const owner = (user.isOwner == true) ? user.username : user.ownerEmail;
      const token = getToken();
      const response = await axiosInstance.post(ENDPOINTS.EVENTS.REMOVE_SCHEDULED_NOTIFICATIONS, {
        token,
        owner,
        id
      });

      if (response.status === 200) {
        toast.success('Record Deleted.', {
          position: "top-right",
          autoClose: 3000,
        });
        return id;
      } else {
        throw new Error('something went wrong');
      }

    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
      });
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);


const eventSlice = createSlice({
  name: "poll",
  initialState: {
    events: [],
    scheduledNotifications: [],
    event: null,
    isLoading: false,
    isModelOpen: false,
    isNotificationModelOpen: false,
    isScheduleNotificationModelOpen: false,
    isEventNotificationModelOpen: false,
    isScheduleEventNotificationModelOpen: false,
    isEditScheduledNotificationModel: false,
    eventId: null
  },
  reducers: {
    openParticipantModel: (state, action) => {
      state.isModelOpen = true;
      state.eventId = action.payload;

    },
    closeParticipantModel: (state) => {
      state.isModelOpen = false;
      state.eventId = null;

    },
    openEventNotificationModel: (state, action) => {
      state.isEventNotificationModelOpen = true;
      state.eventId = action.payload;

    },
    closeEventNotificationModel: (state) => {
      state.isEventNotificationModelOpen = false;
      state.eventId = null;

    },
    openSendNotificationModel: (state, action) => {
      state.isNotificationModelOpen = true;

    },
    closeSendNotificationModel: (state) => {
      state.isNotificationModelOpen = false;

    },
    openScheduleSendNotificationModel: (state, action) => {
      state.isScheduleNotificationModelOpen = true;
      state.event = action.payload;

    },
    closeScheduleSendNotificationModel: (state) => {
      state.isScheduleNotificationModelOpen = false;
      state.event = null;

    },

    openScheduleEventSendNotificationModel: (state, action) => {
      state.isScheduleEventNotificationModelOpen = true;
      state.event = action.payload;

    },
    closeScheduleEventSendNotificationModel: (state) => {
      state.isScheduleEventNotificationModelOpen = false;
      state.event = null;

    },
    openEditScheduledNotificationModel: (state, action) => {
      state.isEditScheduledNotificationModel = true;

      console.log(action.payload)
      state.event = action.payload;

    },
    closeEditScheduledNotificationModel: (state) => {
      state.isEditScheduledNotificationModel = false;
      state.event = null;

    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(getEventsList.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    })
    .addCase(getEventsList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.events = action.payload;
    })
    .addCase(getEventsList.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error?.message || "Failed to fetch events";
    })
      .addCase(getNotificationsList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotificationsList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scheduledNotifications = action.payload?.data;
      })
      .addCase(getNotificationsList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to fetch Notifications";
      })
      .addCase(deleteScheduledNotification.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteScheduledNotification.fulfilled, (state, action) => {
        state.scheduledNotifications = state.scheduledNotifications.filter(
          notification => String(notification.notification_id) !== String(action.payload)
        );
      });

  },
});

export const {
  openParticipantModel,
  closeParticipantModel,
  openEventNotificationModel,
  closeEventNotificationModel,
  openSendNotificationModel,
  closeSendNotificationModel,
  openScheduleSendNotificationModel,
  closeScheduleSendNotificationModel,
  openScheduleEventSendNotificationModel,
  closeScheduleEventSendNotificationModel,
  openEditScheduledNotificationModel,
  closeEditScheduledNotificationModel
} = eventSlice.actions;

export default eventSlice.reducer;
